/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, ReactNode, useContext, useState, useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { RffDrawer } from '@components/RfxDrawers/RffDrawer/RffDrawer';
import { RfiDrawer } from '@components/RfxDrawers/RfiDrawer';
import { RfpDrawer } from '@components/RfxDrawers/RfpDrawer';
import { PriceUpdateDrawer } from '@components/RfxDrawers/PriceUpdateDrawer';

export type RfxDrawerType = 'price_update' | 'rff' | 'rfi' | 'rfp';

export interface PriceUpdateDrawerOptions {
  lockPriceAgreement?: boolean;
  priceAgreementId?: number | null;
}

export interface OpenRfxDrawer {
  (drawer: 'price_update', options?: PriceUpdateDrawerOptions): void;
  (drawer: Exclude<RfxDrawerType, 'price_update'>): void;
}

export interface RfxDrawerContextData {
  closeRfxDrawer: (() => void);
  currentPathName: string;
  isPriceUpdateOpen: boolean;
  isRffOpen: boolean;
  isRfiOpen: boolean;
  isRfpOpen: boolean;
  openRfxDrawer: OpenRfxDrawer;
  priceUpdateOptions: PriceUpdateDrawerOptions;
}

export const contextDefaultValue: RfxDrawerContextData = {
  closeRfxDrawer: () => { },
  currentPathName: '',
  isPriceUpdateOpen: false,
  isRffOpen: false,
  isRfiOpen: false,
  isRfpOpen: false,
  openRfxDrawer: (() => { }) as OpenRfxDrawer,
  priceUpdateOptions: {},
};

export const RfxDrawerContext = createContext<RfxDrawerContextData>(contextDefaultValue);

interface RfxDrawerProviderProps {
  children: ReactNode;
}

export const RfxDrawerProvider = ({ children }: RfxDrawerProviderProps) => {
  const [isRfiOpen, setIsRfiOpen] = useState(false);
  const [isRfpOpen, setIsRfpOpen] = useState(false);
  const [isRffOpen, setIsRffOpen] = useState(false);
  const [isPriceUpdateOpen, setIsPriceUpdateOpen] = useState(false);
  const [priceUpdateOptions, setPriceUpdateOptions] = useState<PriceUpdateDrawerOptions>({});
  const currentPathName = usePathname();

  const openRfxDrawer = useCallback((drawer: RfxDrawerType, options?: PriceUpdateDrawerOptions) => {
    if (drawer === 'rfi') {
      setIsRfpOpen(false);
      setIsRffOpen(false);
      setIsPriceUpdateOpen(false);
      setIsRfiOpen(true);
    } else if (drawer === 'rfp') {
      setIsRfiOpen(false);
      setIsRffOpen(false);
      setIsPriceUpdateOpen(false);
      setIsRfpOpen(true);
    } else if (drawer === 'rff') {
      setIsRfiOpen(false);
      setIsRfpOpen(false);
      setIsPriceUpdateOpen(false);
      setIsRffOpen(true);
    } else if (drawer === 'price_update') {
      setIsRfiOpen(false);
      setIsRfpOpen(false);
      setIsRffOpen(false);
      setPriceUpdateOptions(options ?? {});
      setIsPriceUpdateOpen(true);
    }
  }, []);

  const closeRfxDrawer = useCallback(() => {
    setIsRfiOpen(false);
    setIsRfpOpen(false);
    setIsRffOpen(false);
    setIsPriceUpdateOpen(false);
    setPriceUpdateOptions({});
  }, []);

  const contextValue = useMemo<RfxDrawerContextData>(() => ({
    closeRfxDrawer,
    currentPathName,
    isPriceUpdateOpen,
    isRffOpen,
    isRfiOpen,
    isRfpOpen,
    openRfxDrawer: openRfxDrawer as OpenRfxDrawer,
    priceUpdateOptions,
  }), [closeRfxDrawer, currentPathName, isPriceUpdateOpen, isRffOpen, isRfiOpen, isRfpOpen, openRfxDrawer, priceUpdateOptions]);

  return (
    <RfxDrawerContext.Provider value={contextValue}>
      {children}
      <RffDrawer isOpen={isRffOpen} onClose={closeRfxDrawer} />
      <RfiDrawer isOpen={isRfiOpen} onClose={closeRfxDrawer} />
      <RfpDrawer isOpen={isRfpOpen} onClose={closeRfxDrawer} />
      <PriceUpdateDrawer
        isOpen={isPriceUpdateOpen}
        lockPriceAgreement={priceUpdateOptions.lockPriceAgreement}
        onClose={closeRfxDrawer}
        priceAgreementId={priceUpdateOptions.priceAgreementId ?? null}
      />
    </RfxDrawerContext.Provider>
  );
};

export const useRfxDrawer = (): RfxDrawerContextData => {
  const context = useContext(RfxDrawerContext);

  if (context === undefined) {
    throw new Error('useRfxDrawer must be used within a RfxDrawerProvider');
  }

  return context;
};
